// What a screen needs to know about the chrome around it (nav v2).
//
// TWO SCREENS DREW THEIR OWN TAB BARS before the shell had one: the site page (/sites/:ref?tab=) and the Analytics
// home (/analytics?tab=). Inside nav v2 their tabs ARE the section header's tabs (lib/navMap.ts maps every ?tab= to
// a section and a tab), so each reads `v2` here and leaves its own bar out rather than drawing a second, competing
// one. `actions` is the header's right-hand slot, for the one control a screen owns that belongs up there (the
// Analytics date range).
//
// `useNavVersion` is the fallback switch, kept for one release: ?nav=classic (or the account menu) brings the old
// sidebar back and remembers it; ?nav=v2 returns. New is the default.
import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface NavChrome {
  v2: boolean;
  actions: HTMLElement | null;
}
export const NavChromeContext = createContext<NavChrome>({ v2: false, actions: null });
export const useNavChrome = () => useContext(NavChromeContext);

/** A prefix the shell's addresses live under. "" in the app; the /dev/nav harness mounts the shell at /dev/nav. */
export const NavBaseContext = createContext("");
export const useNavBase = () => useContext(NavBaseContext);

export type NavVersion = "v2" | "classic";
const KEY = "bloxx_nav";

function stored(): NavVersion {
  try {
    return localStorage.getItem(KEY) === "classic" ? "classic" : "v2";
  } catch {
    return "v2";
  }
}

export function useNavVersion(): NavVersion {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const asked = new URLSearchParams(search).get("nav");
  const pick: NavVersion | null = asked === "classic" || asked === "v2" ? asked : null;
  useEffect(() => {
    if (!pick) return;
    try {
      if (pick === "classic") localStorage.setItem(KEY, "classic");
      else localStorage.removeItem(KEY);
    } catch {
      /* private mode: the param still works for this page */
    }
    const rest = new URLSearchParams(search);
    rest.delete("nav");
    navigate(`${pathname}${rest.toString() ? `?${rest}` : ""}${hash}`, { replace: true });
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps
  return pick ?? stored();
}
